import { useMutation, useQuery } from '@tanstack/react-query';
import { IUser } from '../../../@types/model/right/user/userModel';
import { IUserUpsert } from '../../../@types/model/right/user/userUpsert';
import UserHttpService from '../../../service/http/right/userHttpService';
import { DELETE_USER_KEY, GET_USER_KEY, GET_USERS_KEY, LINK_USER_GOOGLE_ACCOUNT, LOGOUT_ALL_USERS_KEY, LOGOUT_USERS_KEY, RESET_EMPLOYEE_CREDENTIALS, UNLINK_USER_EMAIL, UPDATE_EMPLOYEE_CREDENTIALS, UPSERT_USERS_KEY } from '../queryConstants';
import { queryDeleteSuccessHandler, queryErrorHandler, queryNoReturnContentSuccessHandler, querySuccessHandler } from '../queryStateHandlers';


export const useGetUser = (userId : number) => {
    const query = useQuery<IUser, Error>({
        queryKey: [GET_USER_KEY, userId],
        queryFn: () => UserHttpService.get(userId),
        onError: queryErrorHandler('Error loading User.'),
        enabled: false
    });

    return query;
};

export const useGetUsers = (enabled ?: boolean) => {
    const query = useQuery<Array<IUser>, Error>({
        queryKey: [GET_USERS_KEY],
        queryFn: ({ signal }) => UserHttpService.getList(signal),
        onError: queryErrorHandler('Error loading Users.'),
        enabled: enabled ?? true,
    });

    return query;
};

export const useHandleUsersUpsert = () => {
    const result = useMutation<Array<IUser>, Error, Array<IUserUpsert>>({
        mutationKey: [UPSERT_USERS_KEY],
        mutationFn: (upsertData : Array<IUserUpsert>) => UserHttpService.upsert(upsertData),
        onError: queryErrorHandler('Error creating/updating User.'),
        onSuccess: querySuccessHandler([GET_USERS_KEY], 'User created/updated successfully')
    });

    return result;
};

export const useHandleLinkGoogleAccount = () => {
    const result = useMutation<void, Error, string>({
        mutationKey: [LINK_USER_GOOGLE_ACCOUNT],
        mutationFn: (code : string) => UserHttpService.linkGoogleAccount(code),
        onError: queryErrorHandler('Error encountered while trying to link Google Account.'),
        onSuccess: queryNoReturnContentSuccessHandler('Google Account linked successfully.')
    });

    return result;
};

export const useHandleUserUnlinkEmail = () => {
    const result = useMutation<void, Error>({
        mutationKey: [UNLINK_USER_EMAIL],
        mutationFn: () => UserHttpService.unlinkEmail(),
        onError: queryErrorHandler('Error encountered while trying to unlink user(s) email.'),
        onSuccess: queryNoReturnContentSuccessHandler('Unlinked user(s) email successfully.')
    });

    return result;
};

export const useHandleUpdateEmployeeCredentials = () => {
    const result = useMutation<void, Error>({
        mutationKey: [UPDATE_EMPLOYEE_CREDENTIALS],
        mutationFn: () => UserHttpService.updateEmployeeCredentials(),
        onError: queryErrorHandler('Error encountered while trying to update employee credentials.'),
        onSuccess: queryNoReturnContentSuccessHandler('Employee credentials updated successfully.')
    });

    return result;
};

export const useHandleResetEmployeeCredentials = () => {
    const result = useMutation<void, Error>({
        mutationKey: [RESET_EMPLOYEE_CREDENTIALS],
        mutationFn: () => UserHttpService.resetEmployeeCredentials(),
        onError: queryErrorHandler('Error encountered while trying to reset employee credentials.'),
        onSuccess: queryNoReturnContentSuccessHandler('Employee credentials reset successfully.')
    });

    return result;
};

export const useHandleLogoutUsers = () => {
    const result = useMutation<void, Error, Array<number>>({
        mutationKey: [LOGOUT_USERS_KEY],
        mutationFn: (userIds : Array<number>) => UserHttpService.logoutUsers(userIds),
        onError: queryErrorHandler('Error creating/updating User.'),
        onSuccess: queryNoReturnContentSuccessHandler('User(s) logged out successfully.')
    });

    return result;
};

export const useHandleLogoutAllUsers = () => {
    const result = useMutation<void, Error>({
        mutationKey: [LOGOUT_ALL_USERS_KEY],
        mutationFn: () => UserHttpService.logoutAllUsers(),
        onError: queryErrorHandler('Error logging out all users.'),
        onSuccess: queryNoReturnContentSuccessHandler('All users logged out successfully.')
    });

    return result;
};

export const useHandleDeleteUser = (userGuid ?: string) => {
    const result = useMutation<void, Error, number>({
        mutationKey: [DELETE_USER_KEY, userGuid],
        mutationFn: (deleteId : number) => UserHttpService.userDelete(deleteId),
        onError: queryErrorHandler('Error deactivating User.'),
        onSuccess: queryDeleteSuccessHandler([GET_USERS_KEY], 'User successfully deactivated.'),
    });

    return result;
};

